import React, { useEffect, useMemo, useState } from "react";
import { DataGrid, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  gridClasses,
  styled,
} from "@mui/material";
import { Checkbox } from "@mui/material";
import { RxPlus } from "react-icons/rx";
import { CiEdit } from "react-icons/ci";
import { GoTrash } from "react-icons/go";
import { useAuth } from "../../../context/AuthProvider";
import { HttpService } from "../../../service/HttpService";
import FullScreenLoader from "../../../components/Loader/FullScreenLoader";
import DeleteModal from "../../../components/Model/DeleteModal";
import AddEditUnits from "../../../components/Units/AddForm/AddEditUnits";
import { toast } from "react-toastify";

const Units = () => {
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [searchData, setSearchData] = useState("");

  const [open, setOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [clickedUnitId, setClickedUnitId] = useState("");

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [dataId, setDataId] = useState();

  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedId, setSelectedId] = useState([]);
  const [allIds, setAllIds] = useState(0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `inventory/units?page=${
          page + 1
        }&pageSize=${pageSize}&search=${searchData}`
      );
      setRows(response?.data?.data || response?.data || []);
      setRowCount(
        response?.data?.pagination?.totalRecords || response?.data?.length || 0
      );
    } catch (error) {
      // handled in HttpService
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [showLoginPopup, page, pageSize, searchData]);

  const handleOpen = () => {
    setOpen(true);
    setIsAdd(true);
    setClickedUnitId("");
  };

  const handleClose = () => {
    setOpen(false);
    setIsAdd(false);
    setClickedUnitId("");
    setOpenDelete(false);
    fetchUnits();
  };

  const HandleDeleteUnit = async () => {
    try {
      const response = await httpService.delete(
        `${"inventory/units"}/${deleteId}`
      );
      if (response) {
        setOpenDelete(false);
        setDeleteId("");
        fetchUnits();
      }
    } catch (error) {}
  };

  const deleteSelectedUnits = async () => {
    if (selectedId?.length > 0) {
      try {
        // No bulk route: delete each selected id
        for (const id of selectedId) {
          await httpService.delete(`${"inventory/units"}/${id}`);
        }
        fetchUnits();
        setIsAllSelected(false);
        setSelectedId([]);
        setAllIds(0);
        setOpenDeleteModal(false);
      } catch (error) {
        setOpenDeleteModal(false);
      }
    } else {
      setOpenDeleteModal(false);
    }
  };

  const isIdSelected = (id) => selectedId?.includes(id);

  const toggleId = (id) => {
    setSelectedId((prev) => {
      if (!prev) return [id];
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const handleHeaderCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const all = (rows || []).map(
        (row) => row?.id || row?._id || row?.unit_id
      );
      setSelectedId(all);
      setAllIds(all.length);
    } else {
      setSelectedId([]);
    }
    setIsAllSelected(isChecked);
  };

  const StyledGridOverlay = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& .ant-empty-img-1": {
      fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
    },
    "& .ant-empty-img-2": {
      fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
    },
    "& .ant-empty-img-3": {
      fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
    },
    "& .ant-empty-img-4": {
      fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
    },
    "& .ant-empty-img-5": {
      fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
      fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
    },
  }));

  function CustomNoRowsOverlay() {
    return (
      <StyledGridOverlay>
        <Box sx={{ mt: 1 }}>No Data Found</Box>
      </StyledGridOverlay>
    );
  }

  const other = useMemo(
    () => ({
      sx: {
        [`& .${gridClasses.row}`]: {
          fontSize: "14px",
        },
      },
      disableColumnMenu: true,
      slots: { noRowsOverlay: CustomNoRowsOverlay },
      columnHeaderHeight: 45,
      rowHeight: 45,
      showColumnVerticalBorder: true,
    }),
    []
  );

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="department">
        <div className="page-title">
          <div className="title-wrap">
            <h1>Units</h1>
          </div>
          <div className="button-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleOpen}
            >
              <RxPlus /> Add Unit
            </button>
          </div>
        </div>
        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-2">
          <Button
            className="btn btn-delete"
            onClick={() => {
              if (selectedId?.length > 0) {
                setOpenDeleteModal(true);
              } else {
                toast.error("Please select atleast one unit", {
                  position: "top-right",
                });
              }
            }}
          >
            Delete
          </Button>
        </div>

        <div className="table-filter-wrap">
          <div>
            <div className="header_search_bar common_content">
              <div className="input-group input_group">
                <button className="btn search-btn search_btn">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <circle
                      cx="9.98856"
                      cy="9.98856"
                      r="8.98856"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2402 16.7071L19.7643 20.222"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="search"
                  className="form-control form_control"
                  placeholder="Search..."
                  aria-label="Search"
                  onChange={(e) => setSearchData(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ width: "100%" }}>
          <DataGrid
            rows={rows || []}
            getRowId={(row) => row?.id || row?._id || row?.unit_id}
            loading={loading}
            columns={[
              {
                field: "select_All",
                headerName: "Select All",
                minWidth: 150,
                align: "center",
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                  const thisId =
                    params?.row?.id || params?.row?._id || params?.row?.unit_id;
                  return (
                    <Checkbox
                      value="Checked"
                      checked={
                        isAllSelected
                          ? true
                          : isIdSelected(thisId)
                          ? true
                          : false
                      }
                      onChange={() => toggleId(thisId)}
                    />
                  );
                },
                headerCheckboxSelection: true,
                renderHeader: () => (
                  <>
                    <Checkbox
                      checked={isAllSelected}
                      onChange={(e) => handleHeaderCheckboxChange(e)}
                    />
                    <strong>Select All</strong>
                  </>
                ),
              },
              { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
              // { field: "description", headerName: "Description", flex: 1.2, minWidth: 200 },
            ]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 15 } },
            }}
            paginationMode="server"
            sortingMode="server"
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            rowCount={rowCount}
            pageSizeOptions={[15, 50]}
            rowSelection={false}
            autoHeight
            {...other}
          />
        </div>
      </div>

      {open && (
        <AddEditUnits
          open={open}
          isAdd={isAdd}
          clickedUnitId={clickedUnitId}
          handleClose={() => handleClose()}
          fetchUnits={fetchUnits}
        />
      )}
      <DeleteModal
        open={openDelete}
        HandleClose={() => handleClose()}
        HandleDelete={() => {
          HandleDeleteUnit();
        }}
      />
      {openDeleteModal && (
        <DeleteModal
          open={openDeleteModal}
          HandleClose={() => setOpenDeleteModal(false)}
          HandleDelete={() => {
            deleteSelectedUnits();
          }}
        />
      )}
    </>
  );
};

export default Units;
